import { useContext, useRef } from 'react';
import { fetchTypeaheadSearchGateway } from 'sb-shell/actions/fetchTypeahead';
import { SearchFilterContentTypes, SearchGatewayContentClass, SearchOrigins } from 'search-gateway-client';
import { useDebouncedCallback } from 'use-debounce';

import { NavSearchContext } from '../../context/NavSearchContext';
import { CrossAssetNavSuggestions } from '../../types/shared';
import fetchTypeahead from '../../utils/fetchTypeahead';
import AutoSuggest from '../AutosuggestElements/AutoSuggest';
import { SearchBarVariant, validateKeywords } from './searchUtils';

interface CrossAssetNavSearchBoxProps {
    contentType: SearchFilterContentTypes;
    setSearchTerm: (value: string) => void;
    onSubmit: (contentType: SearchFilterContentTypes, value: string, searchOrigin?: SearchOrigins) => void;
    searchTerm: string;
    useNewSearchUI?: boolean;
    useWarpSearchUI?: boolean;
    placeholder?: string;
    suggestions: any;
    setSuggestions: ({}: Partial<CrossAssetNavSuggestions>) => void;
    clearSuggestions: () => void;
    searchGatewayFetch?: boolean;
    isProminentSearch?: boolean;
}

const searchBarVariantStyle = {
    [SearchBarVariant.ProminentSearch]:
        'unifiedNav-searchBox text-gray-900 w-full h-full justify-start items-center gap-1 inline-flex',
};

// Helper function to convert SearchFilterContentTypes to content class for search gateway
function getContentClass(contentType: SearchFilterContentTypes): SearchGatewayContentClass {
    switch (contentType) {
        case SearchFilterContentTypes.All_audio_content_type:
        case SearchFilterContentTypes.Music:
        case SearchFilterContentTypes.Sound_effects:
            return 'audio';
        case SearchFilterContentTypes.All_videos_content_type:
        case SearchFilterContentTypes.Footage:
        case SearchFilterContentTypes.Motion_bgs:
        case SearchFilterContentTypes.Templates:
        case SearchFilterContentTypes.Premiere_pro_templates:
        case SearchFilterContentTypes.After_effects:
        case SearchFilterContentTypes.Davinci_resolve:
        case SearchFilterContentTypes.Apple_motion:
            return 'video';
        case SearchFilterContentTypes.All_images_content_type:
        case SearchFilterContentTypes.Illustrations:
        case SearchFilterContentTypes.Photos:
        case SearchFilterContentTypes.Vectors:
        default:
            return 'graphic';
    }
}

export default function CrossAssetNavSearchBox({
    contentType,
    setSearchTerm,
    onSubmit,
    searchTerm,
    useNewSearchUI,
    useWarpSearchUI = false,
    suggestions,
    setSuggestions,
    clearSuggestions,
    placeholder,
    searchGatewayFetch = false,
    isProminentSearch,
}: CrossAssetNavSearchBoxProps) {
    const context = useContext(NavSearchContext);
    const { showSuggestions, setShowSuggestions, searchBarVariant } = context;

    const showSuggestionsRef = useRef(showSuggestions);
    // Keep track so we know when suggestions are fetched whether we actually want to render them.
    showSuggestionsRef.current = showSuggestions;

    const loadSuggestions = async (value: string) => {
        // For search gateway, we need to convert contentType to contentClass
        let data;
        if (searchGatewayFetch) {
            const contentClass = getContentClass(contentType);
            data = await fetchTypeaheadSearchGateway(contentClass, value);
        } else {
            data = await fetchTypeahead(contentType, value, true);
        }

        if (!showSuggestionsRef.current) {
            clearSuggestions();
            return;
        }

        if (!validateKeywords(value)) {
            // when keywords are less than 2 characters, render null state search component
            setSuggestions({
                keywords: [],
                relatedCollections: [],
                relatedContentTypesAndKeywords: [],
                latestCollections: data?.latestCollections || [],
                trendingCollections: data?.collections || [],
            });
        } else {
            setSuggestions({
                keywords: data?.keywords || '',
                relatedCollections: data?.collections || [],
                relatedContentTypesAndKeywords: data?.relatedContentTypesAndKeywords || [],
                latestCollections: [],
                trendingCollections: [],
            });
        }
    };

    const handleSuggestionSelected = (
        data: string,
        contentTypeLocal?: SearchFilterContentTypes,
        searchOriginOverride?: SearchOrigins,
    ) => {
        setShowSuggestions(false);
        onSubmit(contentTypeLocal || contentType, data, searchOriginOverride);
    };

    const handleSuggestionsFetchRequested = useDebouncedCallback(loadSuggestions, 250, { maxWait: 1000 });

    const showSuggestionsOnSearchTermChange = (value: string) => {
        setSearchTerm(value);
        setShowSuggestions(true);
        handleSuggestionsFetchRequested(value);
    };

    return (
        <div
            className={
                searchBarVariantStyle[searchBarVariant] ??
                'unifiedNav-searchBox w-full h-full justify-start items-center gap-1 inline-flex'
            }
        >
            <AutoSuggest
                suggestions={suggestions}
                placeholder={placeholder}
                searchTerm={searchTerm}
                showSuggestionsOnSearchTermChange={showSuggestionsOnSearchTermChange}
                setSearchTerm={setSearchTerm}
                onSelectSuggestions={handleSuggestionSelected}
                onClearSuggestions={clearSuggestions}
                contentType={contentType as SearchFilterContentTypes}
                useNewSearchUI={useNewSearchUI}
                useWarpSearchUI={useWarpSearchUI}
                isProminentSearch={isProminentSearch}
            />
        </div>
    );
}
